import { GetStudentOutput } from '@dev-zeromoblt/o-platform-client';
import { axiosInstance } from './instance';
import type { SearchCampusesOutput } from './types/smithy';

export interface CreateStudentInput {
  givenName: string;
  familyName?: string;
  middleName?: string;
  gender?: string;
  birthdate?: string;
  phone?: string;
  campusId: string;
  location: {
    lat: number;
    lng: number;
  };
  geoAddress?: string;
  grade?: string;
  shifts: string[];
  startDate: string;
  status?: string;
  primaryParentId?: string;
  tags?: { [key: string]: string | number };
}

export interface CreateStudentOutput {
  id: string;
  givenName: string;
  familyName?: string;
  [key: string]: any;
}

export type { SearchCampusesOutput };

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const createStudent = async (
  studentData: CreateStudentInput,
  retryCount = 0,
): Promise<CreateStudentOutput> => {
  const maxRetries = 2;

  try {
    const response = await axiosInstance.post('/students', studentData, {
      timeout: 30000,
    });
    return response.data;
  } catch (error: any) {
    console.error(`Student creation error (attempt ${retryCount + 1}):`, error);

    const isRetryableError =
      error.code === 'ECONNABORTED' ||
      error.code === 'NETWORK_ERROR' ||
      (error.response && error.response.status >= 500) ||
      !error.response;

    if (isRetryableError && retryCount < maxRetries) {
      await delay(2000);
      return createStudent(studentData, retryCount + 1);
    }

    if (error.code === 'ECONNABORTED') {
      throw new Error(
        'Request timeout. The student might have been created. Please check and try again if needed.',
      );
    } else if (error.response) {
      const status = error.response.status;
      const message =
        error.response.data?.message ||
        error.response.data?.error ||
        `Server error: ${status}`;

      if (status === 409) {
        throw new Error('Student with this information already exists.');
      } else if (status === 400) {
        throw new Error(`Invalid student data: ${message}`);
      } else {
        throw new Error(message);
      }
    } else if (error.request) {
      throw new Error(
        'Network error. The student might have been created. Please refresh to check.',
      );
    } else {
      throw new Error(error.message || 'An unexpected error occurred');
    }
  }
};

export const searchCampuses = async (
  query?: string,
  offset?: number,
  limit?: number,
): Promise<SearchCampusesOutput> => {
  try {
    const searchQuery = query || '*';
    const response = await axiosInstance.get('/campuses/search', {
      params: {
        query: searchQuery,
        offset,
        limit: limit || 500,
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updatePhoneOfStudent = async (
  studentId: string,
  phone: string,
): Promise<SearchCampusesOutput> => {
  try {
    const body = JSON.stringify({
      phone,
    });
    const response = await axiosInstance.put(
      `/students/${studentId}/phone`,
      body,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export interface UpdateStudentInput {
  primaryParentId?: string;
  givenName?: string;
  familyName?: string;
  gender?: string;
  campusId?: string;
  grade?: string;
  shifts?: string[];
  startDate?: string;
  status?: string;
  location?: {
    lat: number;
    lng: number;
  };
  geoAddress?: string;
  tags?: { [key: string]: string | number };
}

export const getStudentById = async (studentId: string): Promise<GetStudentOutput> => {
  try {
    const response = await axiosInstance.get(`/students/${studentId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching student:', error);
    throw error;
  }
};

export const updateStudent = async (
  studentId: string,
  studentData: UpdateStudentInput,
): Promise<any> => {
  try {
    const response = await axiosInstance.put(`/students/${studentId}`, studentData);
    return response.data;
  } catch (error: any) {
    console.error('Error updating student:', error);
    if (error.response) {
      const message = error.response.data?.message || 'Failed to update student';
      throw new Error(message);
    }
    throw error;
  }
};
